import { useEffect, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import {
  DEFAULT_FLAGS,
  deriveFlags,
  type FeatureFlag,
  type FeatureFlagRow,
} from './featureFlags';

/**
 * Reads the feature-flag backbone (B5).
 *
 * - Table readable → per-flag values; a missing row is OFF (fail-closed).
 * - Table unreadable / unprovisioned (migration not applied) or no client →
 *   fall back to the seeded safe posture (`DEFAULT_FLAGS`), NOT all-off, so a
 *   read failure or pre-migration deploy can't brick the app. Risky
 *   capabilities are off in that posture, so safety-critical flags fail safe.
 */
export const useFeatureFlags = () => {
  const [flags, setFlags] = useState<Record<FeatureFlag, boolean>>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const supabase = getSupabase();
    if (!supabase) {
      setLoading(false); // no backend (dev/mock) → seeded safe posture
      return;
    }

    supabase
      .from('hottr_feature_flags')
      .select('key, enabled')
      .then(({ data, error }) => {
        if (cancelled) return;
        setFlags(error ? DEFAULT_FLAGS : deriveFlags(data as FeatureFlagRow[] | null));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    flags,
    loading,
    isEnabled: (key: FeatureFlag): boolean => flags[key] === true,
  };
};
